import { useEffect, useState, useCallback } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useStateContext } from "../context/ContextProvider";
import axiosClient from "../axiosClient";
import { useTranslation } from "react-i18next";

const SearchUsers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCriteria, setSearchCriteria] = useState("username");
  const [users, setUsers] = useState([]);
  const [searchedUsers, setSearchedUsers] = useState(users);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useStateContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSearch = useCallback(() => {
    const filteredUsers = users.filter((user) => {
      const value = String(user[searchCriteria]).toLowerCase();
      return value.includes(searchTerm.toLowerCase());
    });
    setSearchedUsers(filteredUsers);
  }, [searchCriteria, searchTerm, users]);

  useEffect(() => {
    if (!token) {
      setError("User is not authenticated. Please log in.");
      return;
    }

    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axiosClient.get("api/print_all_users/");
        setUsers(data.custom_users);
      } catch (error) {
        setError(
          error.response?.data?.message || "An error occurred while fetching users."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [token]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  const getRoleDisplay = (role) => {
    switch (role) {
      case "ADMIN":
        return "Secretary (Admin)";
      case "STAFF":
        return "Doctor (Staff)";
      case "CLIENT":
        return "Patient (Client)";
      default:
        return "Non Declared";
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Card
        elevation={isMobile ? 4 : 16}
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 900 },
          mx: { xs: 0, sm: 2 },
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            sx={{ mb: { xs: 2, sm: 3 }, textAlign: "center", fontWeight: "medium" }}
          >
            {t("Search Users")}
          </Typography>
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>{t("Search By")}</InputLabel>
                <Select
                  value={searchCriteria}
                  onChange={(e) => setSearchCriteria(e.target.value)}
                  label={t("Search By")}
                >
                  {/* <MenuItem value="username">{t("Username")}</MenuItem> */}
                  <MenuItem value="first_name">{t("First Name")}</MenuItem>
                  <MenuItem value="last_name">{t("Last Name")}</MenuItem>
                  {/* <MenuItem value="id">{t("ID Number")}</MenuItem> */}
                  {/* <MenuItem value="id_card">{t("ID Card")}</MenuItem> */}
                  <MenuItem value="phone_number">{t("Phone Number")}</MenuItem>
                  <MenuItem value="email">{t("Email")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Search Term")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size={isMobile ? "small" : "medium"}
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 2, sm: 3 } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              size={isMobile ? "medium" : "large"}
              sx={{ px: isMobile ? 3 : 4 }}
            >
              {t("Search")}
            </Button>
          </Box>
          {loading && (
            <Typography sx={{ textAlign: "center", mb: 2, color: "text.secondary" }}>
              Loading...
            </Typography>
          )}
          {error && (
            <Typography
              color="error"
              sx={{ textAlign: "center", mb: 2, fontSize: { xs: "0.875rem", sm: "1rem" } }}
            >
              {error}
            </Typography>
          )}
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: { xs: 300, sm: 500 },
              overflowY: "auto",
              overflowX: "auto",
              boxShadow: "none",
            }}
          >
            <Table
              size={isMobile ? "small" : "medium"}
              stickyHeader
              sx={{
                minWidth: isMobile ? 600 : 650,
                "& .MuiTableCell-head": {
                  bgcolor: "grey.700",
                  color: "grey.200",
                  fontWeight: "medium",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  py: isMobile ? 1 : 1.5,
                },
                "& .MuiTableCell-body": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  py: isMobile ? 0.75 : 1,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t("Name")}</TableCell>
                  <TableCell>{t("Surname")}</TableCell>
                  <TableCell>{t("Email")}</TableCell>
                  <TableCell>{t("Username")}</TableCell>
                  <TableCell>{t("Phone Number")}</TableCell>
                  <TableCell>{t("Role")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchedUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{
                      "& .MuiTableCell-body": {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      },
                    }}
                  >
                    <TableCell>{user.first_name}</TableCell>
                    <TableCell>{user.last_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.phone_number}</TableCell>
                    <TableCell>{getRoleDisplay(user.user_role)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SearchUsers;